import { ENDPOINT_URL, FLUSH_INTERVAL_MS, MAX_BUFFER_SIZE, isProd } from './config'
import { sendBatch, sendBatchBeacon, drainRetryQueue, clearRetryQueue, randomId } from './transport'
import { emit } from './inspector'

class Tracker {
  constructor() {
    this.buffer = []
    this.flushTimer = null
    this.started = false
    this.lastTickAt = null
  }

  // A snapshot for the insights panel.
  getState() {
    return {
      enabled: Boolean(ENDPOINT_URL),
      started: this.started,
      buffered: this.buffer.length,
      lastTickAt: this.lastTickAt,
      intervalMs: FLUSH_INTERVAL_MS,
      maxBuffer: MAX_BUFFER_SIZE,
    }
  }

  start() {
    // No URL means tracking is off for this build.
    if (this.started || !ENDPOINT_URL) return
    this.started = true
    this.lastTickAt = Date.now()

    this.flushTimer = setInterval(() => {
      this.lastTickAt = Date.now()
      emit('tick', { at: this.lastTickAt, buffered: this.buffer.length })
      this._flushAsync('interval')
      drainRetryQueue()
    }, FLUSH_INTERVAL_MS)

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this._flushBeacon()
    })
    window.addEventListener('pagehide', () => this._flushBeacon())
  }

  track(name, props = {}) {
    // Don't fill the buffer with events that will never be sent.
    if (!ENDPOINT_URL) return
    if (!name || typeof name !== 'string') {
      if (!isProd()) console.warn('[tracking] track() called without a valid name')
      return
    }

    try {
      const envelope = {
        eventId: randomId(),
        name,
        occurredAt: new Date().toISOString(),
        props,
      }

      this.buffer.push(envelope)
      emit('tracked', { event: envelope, buffered: this.buffer.length })

      if (this.buffer.length >= MAX_BUFFER_SIZE) {
        this._flushAsync('size')
      }
    } catch (err) {
      // Tracking should never break the page.
      if (!isProd()) console.warn('[tracking] track() failed, dropping event', err)
    }
  }

  flush() {
    this._flushAsync('manual')
  }

  _flushAsync(reason) {
    if (this.buffer.length === 0) return
    const events = this.buffer
    this.buffer = []
    const batchId = randomId()
    emit('flush', { batchId, reason, count: events.length, at: Date.now() })
    sendBatch(events)
      .then((outcome) => emit('sent', { batchId, outcome, count: events.length, at: Date.now() }))
      .catch(() => emit('sent', { batchId, outcome: 'failed', count: events.length, at: Date.now() }))
  }

  _flushBeacon() {
    if (this.buffer.length === 0) return
    const events = this.buffer
    this.buffer = []
    emit('flush', { batchId: randomId(), reason: 'pagehide', count: events.length, at: Date.now() })
    sendBatchBeacon(events)
  }
}

export const tracker = new Tracker()

export function initTracking() {
  tracker.start()
}

export function track(name, props) {
  tracker.track(name, props)
}

export function flush() {
  tracker.flush()
}

export function flushBeacon() {
  tracker._flushBeacon()
}

// Call on logout, before the user is cleared.
export async function flushAll() {
  tracker.flush()
  await drainRetryQueue()
  clearRetryQueue()
}
